import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Locale } from "../shared/dto";
import { NotificationCenter } from "./NotificationCenter";
import {
  ClientContext,
  ClientCockpitHero,
  ServiceDrawer,
  StaffJourneyRail,
  StaffNavigation,
  StaffSurfaceState,
  StaffToday,
  nextBestStaffAction,
  staffStepsWithLifecycle,
  staffMutationOutcome,
  staffRouteFromSearch,
  type StaffRouteView
} from "./staff";
import { AdminTools } from "./staff/AdminTools";
import { ClientList, STAFF_TEST_FILTER_STORAGE_KEY } from "./staff/ClientList";
import { storeStaffTestFilter } from "./staff/test-filter";
import { filterClientsByPartner, type PartnerFilter } from "./staff/partner-filter";
import { MyProfilePanel, PaneBoundary } from "./staff/ConsolePanels";
import { ClientSections, clientSectionElementId } from "./staff/ClientTools";
import { PhaseWorkspacePane } from "./staff/DetailPane";
import {
  STAFF_DETAIL_POLL_MS,
  STAFF_WAITING_ALERT_DAYS,
  isStaffDetailRequestCurrent,
  nextStaffDetailRequestVersion,
  selectedStaffClientAfterMatrixLoad,
  staffMutation,
  type ClientSectionKey,
  type StaffDetail,
  type StaffMatrixClient,
  type StaffMutationOptions,
  type StaffRole
} from "./staff/model";
import {
  clientInitial,
  staffCurrentStep,
  staffCurrentStepTitle
} from "./staff/core";

export {
  STAFF_DETAIL_POLL_MS,
  STAFF_WAITING_ALERT_DAYS,
  isStaffDetailRequestCurrent,
  nextStaffDetailRequestVersion,
  selectedStaffClientAfterMatrixLoad,
  staffMutation
} from "./staff/model";
export type {
  StaffMatrixClient,
  StaffMutationOptions,
  StaffRailStep,
  StaffRun,
  StaffStepStatus
} from "./staff/model";
export {
  StaffClientAvatar,
  StaffStepLegend,
  StaffStepOverview,
  StaffStepStrip
} from "./staff";
export {
  orderedSections,
  staffCurrentStep,
  staffStepTitle
} from "./staff/core";
export { callActionAvailability, deliverableActionAvailability, issueOneTimeCode } from "./staff/CallsPane";
export { ConfirmationUndoPanel, StableCallLinksCard, confirmationUndoTargets } from "./staff/Editors";
export type { ConfirmationUndoTarget } from "./staff/Editors";
export { callCodeStateKey } from "./staff/PhaseCards";
export { FeedbackPane } from "./staff/FeedbackPane";
export { StaffNoteChip, staffChipTitle } from "./staff/FeedbackNotes";
export { staffAnswerSortRank } from "./staff/ConsolePanels";
export { DFY_GATES, DfyRunPanel, dfyGateView, dfyStepLabel, dfyStrategyStepLabelKey, isDfyRunStale, mergeDfySteps } from "./staff/DfyBuild";
export { BACKFILL_STEPS } from "./staff/Operations";

export function StaffConsole(props: {
  role: StaffRole;
  csrfToken: string;
  locale: Locale;
  // F116 left the console with no way to change its own language: the switch
  // was on the client Account surface, which `Dashboard` never reaches for a
  // partner or an admin. The console asks the app to re-render in the new
  // language exactly the way the client surfaces do.
  onLocaleChange: (locale: Locale) => void;
  t: (key: string) => string;
  onLogout: () => void | Promise<void>;
  initialMatrix?: StaffMatrixClient[];
  email?: string;
  displayName?: string | null;
  currentUserId?: string;
}) {
  const [clients, setClients] = useState<StaffMatrixClient[]>(props.initialMatrix ?? []);
  const [loading, setLoading] = useState(props.initialMatrix === undefined);
  const initialRoute = typeof window === "undefined"
    ? { view: props.initialMatrix ? "clients" as const : "today" as const, clientId: null, step: null, tool: null }
    : staffRouteFromSearch(window.location.search);
  // A `?staff=admin` address in a partner's browser lands on their own Dnes
  // rather than on an empty page they may not have.
  const [view, setView] = useState<StaffRouteView>(
    initialRoute.view === "admin" && props.role !== "admin" ? "today" : initialRoute.view
  );
  const [adminTool, setAdminTool] = useState<string | null>(initialRoute.tool);
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    initialRoute.clientId);
  const [selectedStep, setSelectedStep] = useState<string | null>(initialRoute.step);
  const [detail, setDetail] = useState<StaffDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // F138a: the nine tabs and the drawer that hid them are gone. The three
  // sections below the work are ALWAYS rendered, so a control that used to
  // switch a tab (and had to open a drawer as well - B2, audit F115) now just
  // brings its section into view. Nothing to open, nothing to remember.
  const openSectionFromWorkspace = useCallback((section: ClientSectionKey) => {
    if (typeof document === "undefined") return;
    const target = document.getElementById(clientSectionElementId(section));
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    // The heading is the destination, so focus goes to the section itself
    // (tabIndex -1): a screen reader lands where the eye does.
    target.focus({ preventScroll: true });
  }, []);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"pending" | "calls" | "unassigned" | "stuck" | null>(null);
  // F173: an admin narrows the list to one partner's clients. A partner only
  // ever receives their own rows, so for them the filter stays null.
  const [partnerFilter, setPartnerFilter] = useState<PartnerFilter>(null);
  const [hideTestTenants, setHideTestTenants] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      return window.localStorage.getItem(STAFF_TEST_FILTER_STORAGE_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [hiddenDeepLink, setHiddenDeepLink] = useState<{ clientId: string; name: string } | null>(null);
  const [message, setMessage] = useState<"idle" | "saved" | "error" | "conflict" | "offline">("idle");
  const detailRequest = useRef(0);
  const detailRef = useRef<HTMLElement | null>(null);
  // Kterého klienta chce okno ukázat. Drží se, dokud detail toho klienta
  // opravdu nedojede - viz scroll efekt níž. `scrollTick` je tu proto, že
  // "Otevřít klienta" na UŽ otevřeném klientovi nezmění žádný stav, a přesto
  // musí okno doskočit (ovladač je i v Servisu pod detailem).
  const scrollIntent = useRef<string | null>(initialRoute.view === "detail" ? initialRoute.clientId : null);
  const [scrollTick, setScrollTick] = useState(0);

  const writeRoute = useCallback((nextView: StaffRouteView, clientId = selectedId, step = selectedStep) => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams();
    if (nextView === "clients") params.set("staff", "clients");
    if (nextView === "admin") params.set("staff", "admin");
    if (nextView === "detail" && clientId) {
      params.set("client", clientId);
      if (step) params.set("staffStep", step);
    }
    const search = params.toString();
    window.history.replaceState(null, "", search ? `/?${search}` : "/");
  }, [selectedId, selectedStep]);

  const navigate = useCallback((nextView: StaffRouteView) => {
    if (nextView === "detail" && !selectedId) return;
    // A partner cannot reach the administration surface even by typing the
    // address: the server refuses every admin route anyway, so the console
    // must not draw a page that would answer nothing but refusals.
    if (nextView === "admin" && props.role !== "admin") return;
    setView(nextView);
    // Leaving Správa drops the `?tool=` that opened one of its panels: the
    // deep link is about the arrival, not a filter the operator carries around.
    if (nextView !== "admin") setAdminTool(null);
    writeRoute(nextView);
  }, [props.role, selectedId, writeRoute]);

  const openClient = useCallback((clientId: string, step: string | null = null) => {
    setSelectedId(clientId);
    setSelectedStep(step);
    setView("detail");
    writeRoute("detail", clientId, step);
    scrollIntent.current = clientId;
    setScrollTick((tick) => tick + 1);
  }, [writeRoute]);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/staff/clients", { credentials: "include" });
      if (!response.ok) throw new Error("staff_matrix_failed");
      const body = await response.json() as { clients: StaffMatrixClient[] };
      setClients(body.clients);
      setMessage("idle");
    } catch {
      setMessage("error");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (clientId: string, options: { background?: boolean } = {}) => {
    const request = nextStaffDetailRequestVersion(detailRequest.current);
    detailRequest.current = request;
    // Background refresh (mutations, Alex-edit polling) swaps the detail IN
    // PLACE - blanking it would unmount open readers mid-work (Pavel
    // 2026-07-23: the material must change before their eyes, not close).
    if (!options.background) {
      setDetailLoading(true);
      setDetail(null);
    }
    try {
      const response = await fetch(`/api/staff/clients/${clientId}`, { credentials: "include" });
      if (!response.ok) throw new Error("staff_detail_failed");
      const body = await response.json() as StaffDetail;
      if (isStaffDetailRequestCurrent(request, detailRequest.current)) {
        setDetail(body);
        setMessage("idle");
      }
    } catch {
      if (isStaffDetailRequestCurrent(request, detailRequest.current)) {
        if (!options.background) setDetail(null);
        setMessage("error");
      }
    } finally {
      if (isStaffDetailRequestCurrent(request, detailRequest.current)) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (props.initialMatrix === undefined) void loadMatrix();
  }, [loadMatrix, props.initialMatrix]);

  useEffect(() => {
    const visibleSelection = selectedStaffClientAfterMatrixLoad(
      selectedId,
      clients,
      loading,
      hideTestTenants
    );
    if (selectedId && visibleSelection === null) {
      // Invalidate any detail request already in flight before clearing the
      // pane. Its late response must not repopulate a filtered test tenant.
      detailRequest.current = nextStaffDetailRequestVersion(detailRequest.current);
      // A deep link to a filtered client must SAY so, not silently drop the
      // admin into the matrix without a trace (audit a3-06).
      const hidden = clients.find((client) => client.id === selectedId && client.is_test);
      setHiddenDeepLink(hidden && hideTestTenants ? { clientId: hidden.id, name: hidden.name } : null);
      setSelectedId(null);
      setView("clients");
      writeRoute("clients", null, null);
      setDetail(null);
      setDetailLoading(false);
    }
  }, [clients, hideTestTenants, loading, selectedId, writeRoute]);

  // The console lives open all day: keep pending counts and phases truthful
  // (matrix is one cheap batched call since E1), plus refresh on tab focus.
  useEffect(() => {
    if (props.initialMatrix !== undefined) return;
    const tick = window.setInterval(() => {
      if (!document.hidden) void loadMatrix();
    }, 60_000);
    const onVisible = () => { if (!document.hidden) void loadMatrix(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.clearInterval(tick); document.removeEventListener("visibilitychange", onVisible); };
  }, [loadMatrix, props.initialMatrix]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
  }, [loadDetail, selectedId]);

  // Detail liveness (Pavel 2026-07-24: "aby se tam objevili sami, abych
  // nemusel aktualizovat stránku"): same pattern as the matrix - poll while
  // visible plus refresh on tab focus. Background swap keeps open readers and
  // overlays mounted; the Podněty pane and its tab badge recompute from the
  // fresh detail.
  useEffect(() => {
    if (!selectedId) return;
    const tick = window.setInterval(() => {
      if (!document.hidden) void loadDetail(selectedId, { background: true });
    }, STAFF_DETAIL_POLL_MS);
    const onVisible = () => { if (!document.hidden) void loadDetail(selectedId, { background: true }); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.clearInterval(tick); document.removeEventListener("visibilitychange", onVisible); };
  }, [loadDetail, selectedId]);

  // "Otevřít klienta" přepne plochu, ale seznam klientů i otevřený Servis nad
  // ní bývají desetitisíce pixelů dlouhé: dokud okno nedoskočí, kliknutí
  // vypadá jako mrtvý řádek (obrazovka se opticky nezmění). Plynulý posun
  // spuštěný ve stejném snímku prohlížeč zruší ve chvíli, kdy dojetý detail
  // změní výšku dokumentu - kotvení posunu vrátí okno na původní místo, jen
  // o dvacet tisíc pixelů níž. Proto skok drží záměr: opakuje se při každém
  // překreslení, dokud na obrazovce není detail toho klienta, o který si
  // uživatel řekl. Odsazení pod lepivou hlavičku řeší scroll-margin-top
  // u .staff-detail.
  useEffect(() => {
    const wanted = scrollIntent.current;
    if (wanted === null || view !== "detail") return;
    detailRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    if (detail?.client.id === wanted) scrollIntent.current = null;
  }, [detail, detailLoading, scrollTick, view]);

  const refresh = useCallback(async () => {
    await loadMatrix();
    if (selectedId) await loadDetail(selectedId, { background: true });
  }, [loadDetail, loadMatrix, selectedId]);

  const run = useCallback(async (
    path: string,
    options: Omit<StaffMutationOptions, "csrfToken">,
    behavior: { refresh?: boolean } = {}
  ) => {
    setMessage("idle");
    const response = await staffMutation(path, { ...options, csrfToken: props.csrfToken });
    if (!response.ok) {
      setMessage(staffMutationOutcome(response.status));
      return response;
    }
    setMessage("saved");
    if (behavior.refresh !== false) await refresh();
    return response;
  }, [props.csrfToken, refresh]);

  const stuck = useCallback((client: StaffMatrixClient) =>
    client.phaseAgeDays > 14 && client.currentPhase !== "app", []);
  const waitingOverdue = useCallback((client: StaffMatrixClient) =>
    client.pendingFeedback > 0 && (client.pendingFeedbackAgeDays ?? 0) > STAFF_WAITING_ALERT_DAYS, []);
  const matrixClients = useMemo(
    () => hideTestTenants ? clients.filter((client) => !client.is_test) : clients,
    [clients, hideTestTenants]
  );
  // The list, its count and its metric filters all answer about the chosen
  // partner's clients; Dnes keeps the whole team's queue.
  const partnerClients = useMemo(
    () => props.role === "admin" ? filterClientsByPartner(matrixClients, partnerFilter) : matrixClients,
    [matrixClients, partnerFilter, props.role]
  );
  const visibleClients = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(props.locale);
    let list = partnerClients;
    if (needle) list = list.filter((client) =>
      `${client.name} ${client.email} ${staffCurrentStepTitle(client.steps, props.t)}`.toLocaleLowerCase(props.locale).includes(needle));
    if (filter === "pending") list = list.filter((client) => client.pendingFeedback > 0);
    if (filter === "calls") list = list.filter((client) => client.nextCall);
    if (filter === "unassigned") list = list.filter((client) => !client.partner);
    if (filter === "stuck") list = list.filter(stuck);
    // Triage order, not the alphabet: who waits on us first (open feedback,
    // most first), then stuck clients, then the rest by name.
    return [...list].sort((a, b) =>
      (b.pendingFeedback - a.pendingFeedback)
      || (Number(stuck(b)) - Number(stuck(a)))
      || a.name.localeCompare(b.name, props.locale));
  }, [filter, partnerClients, props.locale, query, stuck]);

  const activePhaseStep = detail
    ? selectedStep ?? staffCurrentStep(detail.steps)?.key ?? "home"
    : selectedStep ?? "home";
  const displaySteps = detail ? staffStepsWithLifecycle(detail) : [];

  return (
    <main className="staff-console">
      {/* An operator tool opens on the work, not on a headline. The marketing
          hero ("Tvoji klienti na jednom místě" + lead) cost ~250 px on EVERY
          surface, including a client detail where it made no sense at all
          (audit P1). The page heading is now the surface the partner is on. */}
      <header className="staff-header staff-header-compact">
        <div className="staff-brand">
          <img className="brand-logo staff-logo" src="/assets/buddypro-logo.png" alt={props.t("common.brandAlt")} />
          <StaffNavigation
            active={view}
            detailDisabled={!selectedId}
            onNavigate={navigate}
            showAdmin={props.role === "admin"}
            t={props.t}
          />
        </div>
        <div className="staff-header-actions">
          <NotificationCenter
            activeClientId={`staff:${props.currentUserId ?? props.email ?? props.role}`}
            csrfToken={props.csrfToken}
            locale={props.locale}
            t={props.t}
          />
          {/* Login identity lives with the header controls, never as a fixed
              strip overlaying the work surface (night round N12). One row,
              one visual grammar (N35): badge + e-mail + two quiet actions.
              The old stacked identity column beside a boxed button pushed
              "Odhlásit" onto its own stretched wrap line. */}
          {/* Identity = the person, not their login string. A truncated
              "m6-demo-partner-2026...@exa..." told the partner nothing and the
              avatar took its initial from the e-mail ("M" for Tomáš). */}
          <div className="staff-identity-desktop">
            <span className={`role-badge ${props.role}`}>{props.t(`staffConsole.role.${props.role}`)}</span>
            <span className="staff-identity-name" title={props.email}>
              {props.displayName?.trim() || props.email}
            </span>
            <button className="staff-account-action" type="button" onClick={() => setProfileOpen((open) => !open)}>
              {props.t("staffConsole.myProfile.open")}
            </button>
            <button className="staff-account-action" type="button" onClick={() => void props.onLogout()}>{props.t("common.logout")}</button>
          </div>
          <details className="staff-avatar-menu">
            <summary aria-label={props.t("staffConsole.accountMenu")}>
              {clientInitial(props.displayName ?? "", props.email ?? "")}
            </summary>
            <div>
              <span className={`role-badge ${props.role}`}>{props.t(`staffConsole.role.${props.role}`)}</span>
              <span className="staff-identity-name">{props.displayName?.trim() || ""}</span>
              {props.email && <span className="staff-identity-email" title={props.email}>{props.email}</span>}
              <button className="staff-account-action" type="button" onClick={() => setProfileOpen((open) => !open)}>
                {props.t("staffConsole.myProfile.open")}
              </button>
              <button className="staff-account-action" type="button" onClick={() => void props.onLogout()}>{props.t("common.logout")}</button>
            </div>
          </details>
        </div>
      </header>

      {profileOpen && <MyProfilePanel csrfToken={props.csrfToken} locale={props.locale} onLocaleChange={props.onLocaleChange} t={props.t} onClose={() => setProfileOpen(false)} />}

      {view === "today" && (
        <StaffToday
          clients={matrixClients}
          hideTestTenants={hideTestTenants}
          onHideTestTenantsChange={setHideTestTenants}
          locale={props.locale}
          onOpenClient={(clientId, step) => openClient(clientId, step)}
          role={props.role}
          run={run}
          t={props.t}
        />
      )}

      {view === "clients" && hiddenDeepLink && (
        <div className="staff-hidden-deeplink" role="status">
          <p>
            {props.t("staffConsole.hiddenDeepLink")
              .replace("{name}", hiddenDeepLink.name)}
          </p>
          <button
            className="secondary-button compact-button"
            type="button"
            onClick={() => {
              const target = hiddenDeepLink.clientId;
              setHiddenDeepLink(null);
              setHideTestTenants(false);
              storeStaffTestFilter(false);
              openClient(target);
            }}
          >
            {props.t("staffConsole.hiddenDeepLinkShow")}
          </button>
        </div>
      )}
      {view === "clients" && (
        <ClientList
          filter={filter}
          hideTestTenants={hideTestTenants}
          loading={loading}
          locale={props.locale}
          matrixClients={partnerClients}
          allClients={matrixClients}
          partnerFilter={partnerFilter}
          setPartnerFilter={setPartnerFilter}
          openClient={openClient}
          query={query}
          role={props.role}
          selectedId={selectedId}
          setFilter={setFilter}
          setHideTestTenants={setHideTestTenants}
          setQuery={setQuery}
          stuck={stuck}
          t={props.t}
          visibleClients={visibleClients}
          waitingOverdue={waitingOverdue}
        />
      )}

      {view === "detail" && (
      <section className="staff-detail" ref={detailRef} aria-labelledby="staff-detail-title">
        <div className="staff-detail-head">
          <div>
            {/* Way back to the list. Switching tabs loses the thread; a
                breadcrumb keeps it (audit P1). */}
            <button
              className="staff-breadcrumb"
              onClick={() => navigate("clients")}
              type="button"
            >
              <span aria-hidden="true">‹</span>
              {props.t("partnerOS.navigation.clients")}
            </button>
            <h1 id="staff-detail-title">{detail?.client.name ?? props.t("staffConsole.selectClient")}</h1>
          </div>
          {detail && (
            <span className="phase-label">
              {staffCurrentStepTitle(displaySteps, props.t)}
            </span>
          )}
        </div>
        {detailLoading && (
          <StaffSurfaceState
            body={props.t("partnerOS.states.detailLoadingBody")}
            title={props.t("partnerOS.states.detailLoadingTitle")}
            variant="loading"
          />
        )}
        {!detailLoading && !detail && (
          <StaffSurfaceState
            body={props.t("staffConsole.selectHint")}
            title={props.t("staffConsole.selectClient")}
            variant="empty"
          />
        )}
        {!detailLoading && detail && (
          <div className="staff-client-cockpit">
            <StaffJourneyRail
              onSelect={(step) => {
                setSelectedStep(step.key);
                writeRoute("detail", detail.client.id, step.key);
              }}
              selectedStep={activePhaseStep}
              steps={displaySteps}
              t={props.t}
            />
            <div className="staff-client-workspace">
              <ClientCockpitHero
                action={nextBestStaffAction(detail, activePhaseStep)}
                secondaryAction={nextBestStaffAction(detail)}
                activeStep={activePhaseStep}
                onOpen={(step) => {
                  setSelectedStep(step);
                  writeRoute("detail", detail.client.id, step);
                }}
                t={props.t}
              />
              <PaneBoundary key={`${detail.client.id}:${activePhaseStep}`} label={props.t("staffConsole.paneError")} retry={props.t("common.retry")}>
                <PhaseWorkspacePane
                  detail={detail}
                  locale={props.locale}
                  role={props.role}
                  run={run}
                  refresh={refresh}
                  csrfToken={props.csrfToken}
                  currentUserId={props.currentUserId}
                  onOpenSection={openSectionFromWorkspace}
                  step={activePhaseStep}
                  t={props.t}
                />
              </PaneBoundary>
            </div>
            {/* F138a: the CRM record is drawn ONCE, in Přehled klienta. It
                used to sit here AND inside the nested drawer, so every client
                showed the same card twice on one page (parity row 24). */}
            <ClientContext
              csrfToken={props.csrfToken}
              detail={detail}
              locale={props.locale}
              role={props.role}
              onOpenStep={(step) => {
                setSelectedStep(step);
                writeRoute("detail", detail.client.id, step);
              }}
              t={props.t}
            />
            {/* F138a: DOLE tři PLOCHÉ sekce. No drawer, no tabs, no nesting -
                the client-side law that a surface is at most one level deep.
                Each section head carries the client's own anatomy (eyebrow,
                one h2, lead) and each is addressable, so a control above can
                bring one into view instead of opening something.
                🔴 F138 kolečko 3: they are a CHILD of the cockpit grid now,
                not a sibling of it. As a sibling they could not begin until
                the tallest column had ended, and the tallest column is the
                seventeen-step rail: 636 px of nothing between the end of the
                step workspace and the head of "Přehled klienta", measured on
                the step where the workspace is shortest. The rail spans both
                rows instead (client-tools.css), so it may run BESIDE the
                sections and the reading column simply continues. */}
            <PaneBoundary
              key={`${detail.client.id}:sections`}
              label={props.t("staffConsole.paneError")}
              retry={props.t("common.retry")}
            >
              <ClientSections
                detail={detail}
                role={props.role}
                locale={props.locale}
                t={props.t}
                run={run}
                refresh={refresh}
                csrfToken={props.csrfToken}
                onOpenStep={(step) => {
                  setSelectedStep(step);
                  writeRoute("detail", detail.client.id, step);
                  detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                currentUserId={props.currentUserId}
              />
            </PaneBoundary>
          </div>
        )}
      </section>
      )}
      {/* F138d: administration is its OWN surface, reached from the navigation
          like every other place in the console. It used to be a drawer
          rendered outside the view branching, so the same thirteen tools hung
          off the end of Dnes, Klienti AND Detail - on Dnes at the bottom of a
          2 792 px page, and 10 409 px once opened. Administration is not
          service equipment of somebody else's page; it is work of its own. */}
      {view === "admin" && props.role === "admin" && (
        <AdminTools t={props.t} locale={props.locale} run={run} refresh={refresh} csrfToken={props.csrfToken}
          clients={matrixClients} hideTestTenants={hideTestTenants}
          onHideTestTenantsChange={(hide) => { setHideTestTenants(hide); storeStaffTestFilter(hide); }}
          currentUserId={props.currentUserId} onOpenClient={(clientId) => openClient(clientId)}
          openTool={adminTool} />
      )}
      <div className="staff-message" aria-live="polite">
        {message === "saved" && <p className="success-message">{props.t("staffConsole.saved")}</p>}
        {message === "error" && (
          <StaffSurfaceState
            body={props.t("partnerOS.states.errorBody")}
            title={props.t("partnerOS.states.errorTitle")}
            variant="error"
          />
        )}
        {message === "conflict" && (
          <StaffSurfaceState
            body={props.t("partnerOS.states.conflictBody")}
            title={props.t("partnerOS.states.conflictTitle")}
            variant="conflict"
          />
        )}
        {/* S2-01: a request that never left the browser is not a server
            error. The operator can act on this one, so it says so. */}
        {message === "offline" && (
          <StaffSurfaceState
            body={props.t("partnerOS.states.offlineBody")}
            title={props.t("partnerOS.states.offlineTitle")}
            variant="error"
          />
        )}
      </div>
    </main>
  );
}
